import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { CloseOutSetOffAndDefault, getOpenFieldsAndSections, toggleOpenFieldSection, UpdatedOpinionField } from '../store';
import { SectionWrapper } from './SectionWrapper';
import { SingleField } from '../fields/SingleField';
import styles from './OpinionSection.module.scss';

interface ClientRelianceCloseOutSetOffAndDefaultProps {
    closeOutSetOffAndDefault: CloseOutSetOffAndDefault;
    previousCloseOutSetOffAndDefault: CloseOutSetOffAndDefault | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
}

export const ClientRelianceCloseOutSetOffAndDefault: React.FC<ClientRelianceCloseOutSetOffAndDefaultProps> = ({ closeOutSetOffAndDefault, previousCloseOutSetOffAndDefault, isEditing, isUpdating, fieldsUpdated }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'closeOutSetOffAndDefault';
    const { cmTriggerEventConsequenceProvisionsEnforceable, conclusionsApplyEqually, provisionsOfCCPDefaultEnforceable, hierarchyOfApplicableEventsEnforceable, setOffProvisionsEnforceable, limitedRecourseProvisionsEnforceable, addendumAdverselyImpactRuleSet, addendumAdverselyImpactISDANettingConclusions, isdaCollateralProviderConclusionsApplyEqually, paragraphsAdverselyImpactConclusions, amendedAddendumEnforceable } = closeOutSetOffAndDefault;

    const getPreviousField = useCallback((fieldId: keyof CloseOutSetOffAndDefault) => !isNull(previousCloseOutSetOffAndDefault) && previousCloseOutSetOffAndDefault[fieldId] || null, [previousCloseOutSetOffAndDefault]);

    return (
        <SectionWrapper id={sectionId} label='Close-out, Set-off, and Default' isEditing={isEditing} isUpdating={isUpdating}>
            <>
                <div className={styles.sectionHeaderLabel}>Under the laws of the jurisdiction under consideration:</div>
                <SingleField
                    field={cmTriggerEventConsequenceProvisionsEnforceable}
                    previousField={getPreviousField('cmTriggerEventConsequenceProvisionsEnforceable')}
                    fieldId='cmTriggerEventConsequenceProvisionsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={conclusionsApplyEqually}
                    previousField={getPreviousField('conclusionsApplyEqually')}
                    fieldId='conclusionsApplyEqually'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={provisionsOfCCPDefaultEnforceable}
                    previousField={getPreviousField('provisionsOfCCPDefaultEnforceable')}
                    fieldId='provisionsOfCCPDefaultEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={hierarchyOfApplicableEventsEnforceable}
                    previousField={getPreviousField('hierarchyOfApplicableEventsEnforceable')}
                    fieldId='hierarchyOfApplicableEventsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={setOffProvisionsEnforceable}
                    previousField={getPreviousField('setOffProvisionsEnforceable')}
                    fieldId='setOffProvisionsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={limitedRecourseProvisionsEnforceable}
                    previousField={getPreviousField('limitedRecourseProvisionsEnforceable')}
                    fieldId='limitedRecourseProvisionsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={addendumAdverselyImpactRuleSet}
                    previousField={getPreviousField('addendumAdverselyImpactRuleSet')}
                    fieldId='addendumAdverselyImpactRuleSet'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={addendumAdverselyImpactISDANettingConclusions}
                    previousField={getPreviousField('addendumAdverselyImpactISDANettingConclusions')}
                    fieldId='addendumAdverselyImpactISDANettingConclusions'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={isdaCollateralProviderConclusionsApplyEqually}
                    previousField={getPreviousField('isdaCollateralProviderConclusionsApplyEqually')}
                    fieldId='isdaCollateralProviderConclusionsApplyEqually'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={paragraphsAdverselyImpactConclusions}
                    previousField={getPreviousField('paragraphsAdverselyImpactConclusions')}
                    fieldId='paragraphsAdverselyImpactConclusions'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={amendedAddendumEnforceable}
                    previousField={getPreviousField('amendedAddendumEnforceable')}
                    fieldId='amendedAddendumEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
            </>
        </SectionWrapper>
    );
};
